import {
  DialogComponent,
  DialogOpenEvent,
  DialogCloseEvent,
} from "@theme/dialog";
import { CartAddEvent, CartUpdateEvent } from "@theme/events";
import { debounce, isMobileBreakpoint } from "@theme/utilities";
import {
  GtmEvents,
  pushGtmEvent,
  mapShopifyCartItems,
} from "@theme/gtm-tracking";

/** @typedef {import('@theme/gtm-tracking').ShopifyCart} ShopifyCart */

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {MutationObserver | null} */
  #rebuyObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(
      DialogOpenEvent.eventName,
      this.#pushCartDrawerOpenedEvent,
    );

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, "");
    }

    bindRebuyCartRefresh();
    this.#rebuyObserver = new MutationObserver(() =>
      initRebuyPlaceholders(this),
    );
    this.#rebuyObserver.observe(this, { childList: true, subtree: true });
    initRebuyPlaceholders(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#rebuyObserver?.disconnect();
    this.#rebuyObserver = null;
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#handleHistoryOpen,
    );
    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleHistoryClose,
    );
    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#pushCartDrawerOpenedEvent,
    );
    this.#historyAbortController?.abort();
  }

  /**
   * Pushes a `cartDrawerOpened` dataLayer event with fresh cart contents.
   */
  #pushCartDrawerOpenedEvent = async () => {
    let response;
    try {
      response = await fetch("/cart.js");
    } catch {
      return;
    }
    if (!response.ok) return;

    /** @type {ShopifyCart} */
    const cart = await response.json();
    pushGtmEvent(GtmEvents.CART_DRAWER_OPENED, {
      cart_drawer: {
        items: mapShopifyCartItems(cart.items),
        cart_total: cart.total_price / 100,
        currency: cart.currency,
      },
    });
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, "");
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener("popstate", this.#handlePopState, {
      signal: this.#historyAbortController.signal,
    });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty(
        "--dialog-drawer-closing-animation",
        "none",
      );
      await this.closeDialog();
      this.refs.dialog.style.removeProperty(
        "--dialog-drawer-closing-animation",
      );
    }
  };

  #handleCartAdd = (event) => {
    // A BOGOS-driven cart:update (SHOPUS-225) is a background sync, not a
    // shopper action — never surprise-open the drawer for it.
    if (event.detail?.data?.source === "bogos") return;

    if (this.hasAttribute("auto-open")) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined("shopify-payment-terms").then(() => {
      const installmentsContent = document.querySelector(
        "shopify-payment-terms",
      )?.shadowRoot;
      const cta = installmentsContent?.querySelector(
        "#shopify-installments-cta",
      );
      cta?.addEventListener("click", this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

/**
 * @typedef {{ init: () => void }} RebuySDK
 */

/**
 * Empty `data-rebuy-id` placeholder Rebuy hydrates into a recommendation
 * carousel. Cart-drawer morphs wipe that DOM (server HTML is the empty div
 * again), so `connectedCallback` re-runs `Rebuy.init()`. Adds from the widget
 * do not go through product-form, so they never dispatch `cart:update` —
 * listen for `rebuy.add` and refresh the drawer section.
 *
 * @extends {HTMLElement}
 */
class RebuyCartWidget extends HTMLElement {
  /** @type {AbortController | null} */
  #readyAbortController = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    bindRebuyCartRefresh();
    this.#observer = new MutationObserver(() => this.#initWidget());
    this.#observer.observe(this, { childList: true, subtree: true });
    this.#initWidget();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#readyAbortController?.abort();
    this.#readyAbortController = null;
  }

  #initWidget() {
    this.#readyAbortController?.abort();
    this.#readyAbortController = new AbortController();
    initRebuyPlaceholders(this);

    if (getRebuy()) return;

    document.addEventListener("rebuy.ready", this.#onRebuyReady, {
      once: true,
      signal: this.#readyAbortController.signal,
    });
  }

  #onRebuyReady = () => {
    getRebuy()?.init();
  };
}

/**
 * @returns {RebuySDK | undefined}
 */
function getRebuy() {
  return /** @type {RebuySDK | undefined} */ (
    /** @type {Window & { Rebuy?: RebuySDK }} */ (window).Rebuy
  );
}

/**
 * @param {ParentNode} root
 */
function initRebuyPlaceholders(root) {
  const placeholder = root.querySelector("[data-rebuy-id]");
  if (!(placeholder instanceof HTMLElement)) return;
  if (placeholder.childElementCount > 0) return;

  const rebuy = getRebuy();
  if (rebuy) {
    rebuy.init();
    return;
  }

  document.addEventListener(
    "rebuy.ready",
    () => {
      getRebuy()?.init();
    },
    { once: true },
  );
}

const refreshCartFromRebuy = debounce(() => {
  document.dispatchEvent(
    new CartUpdateEvent({}, "rebuy-cart-widget", {
      source: "rebuy-cart-widget",
    }),
  );
}, 300);

let rebuyCartRefreshBound = false;

function bindRebuyCartRefresh() {
  if (rebuyCartRefreshBound) return;
  rebuyCartRefreshBound = true;
  document.addEventListener("rebuy.add", refreshCartFromRebuy);
}

if (!customElements.get("cart-drawer-component")) {
  customElements.define("cart-drawer-component", CartDrawerComponent);
}

if (!customElements.get("rebuy-cart-widget")) {
  customElements.define("rebuy-cart-widget", RebuyCartWidget);
}
